import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PersonLinkComponent } from '../../../components/shared/person-link.component';
import { UsersService } from '../../../services/users-service.service';
import {
  EventInsightDetailDto,
  PeerChatDto,
} from '../../../data-transfer-objects/events-insight.dto';

/** What the caller knows when it opens this. */
export interface PeerChatsDialogData {
  eventId: string;
  title: string;
  /** The event page already holds the answer; the board does not. */
  detail: EventInsightDetailDto | null;
}

/** One chat, with the two people named rather than left as uids. */
interface ChatRow {
  id: string;
  people: { uid: string; name: string; messages: number; started: boolean }[];
  messages: number;
  replied: boolean;
  inWindow: boolean;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
}

/**
 * Who actually talked to whom in one space.
 *
 * The 1:1 chats column is the board's outcome metric - a lobby exists to
 * produce connections, and this is the only column that says whether any
 * happened. As a number it could not be acted on: "5 chats, 2 replied" does
 * not say whether five people each found somebody or one member wrote to five
 * strangers and three ignored them, and those are opposite situations for an
 * organizer.
 *
 * Participants, counts and timestamps only. The board's standing promise is
 * that nothing anyone said is read, and a private chat between two members is
 * the last place to start: the admin question is whether the space worked,
 * never what the two of them talked about. There is deliberately no way from
 * here into the messages.
 *
 * Opened both from the board's cell, where it fetches, and from the event
 * page's tile, which already holds the answer and hands it over.
 */
@Component({
  selector: 'app-peer-chats-dialog',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule, PersonLinkComponent],
  templateUrl: './peer-chats-dialog.component.html',
  styleUrls: ['./peer-chats-dialog.component.scss'],
})
export class PeerChatsDialogComponent implements OnInit {
  loading = false;
  error: string | null = null;
  rows: ChatRow[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PeerChatsDialogData,
    private dialogRef: MatDialogRef<PeerChatsDialogComponent>,
    private usersService: UsersService
  ) {}

  ngOnInit(): void {
    if (this.data.detail) {
      this.rows = this.build(this.data.detail);
      return;
    }
    this.loading = true;
    this.usersService.getEventInsight(this.data.eventId, 28).subscribe({
      next: (detail) => {
        this.rows = this.build(detail);
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load the chats: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  /**
   * Names for the uids a chat carries.
   *
   * Both people are members of the space by the time a chat is on this list -
   * that is the rule that put it here - so the detail's own member list is
   * enough and no second lookup is needed.
   */
  private build(detail: EventInsightDetailDto): ChatRow[] {
    const names = new Map(detail.members.map((m) => [m.uid, m.name]));
    return (detail.peerChatList ?? []).map((c: PeerChatDto) => ({
      id: c.id,
      people: c.participants.map((uid) => ({
        uid,
        name: names.get(uid) || uid.slice(0, 8),
        messages: c.perSender?.[uid] ?? 0,
        started: c.initiator === uid,
      })),
      messages: c.messages,
      replied: c.replied,
      inWindow: c.inWindow,
      firstMessageAt: c.firstMessageAt,
      lastMessageAt: c.lastMessageAt,
    }));
  }

  get replied(): number {
    return this.rows.filter((r) => r.replied).length;
  }

  /** A chat one person carried alone: they wrote, nobody wrote back. */
  get unanswered(): number {
    return this.rows.filter((r) => !r.replied).length;
  }

  ago(iso: string | null): string {
    if (!iso) return '—';
    const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  close(): void {
    this.dialogRef.close();
  }
}
